/**
 * Master/Detail Core Logic
 *
 * Pure functions for managing detail row expansion state.
 */

/* eslint-disable @typescript-eslint/no-explicit-any */
// Uses `any` for maximum flexibility with user-defined row types.

import { setSanitizedHTML } from '../../core/internal/sanitize';

/**
 * Expand a detail row.
 *
 * **Mutates the input `Set` in place and returns the same reference** (avoids
 * O(n) copy). Reference identity is preserved across calls.
 */
export function expandDetailRow(expandedRows: Set<object>, row: object): Set<object> {
  expandedRows.add(row);
  return expandedRows;
}

/**
 * Collapse a detail row.
 *
 * **Mutates the input `Set` in place and returns the same reference** (avoids
 * O(n) copy). Reference identity is preserved across calls.
 */
export function collapseDetailRow(expandedRows: Set<object>, row: object): Set<object> {
  expandedRows.delete(row);
  return expandedRows;
}

/**
 * Check if a detail row is expanded.
 */
export function isDetailExpanded(expandedRows: Set<object>, row: object): boolean {
  return expandedRows.has(row);
}

/**
 * Create a detail element for a given row.
 * The element spans all columns and contains the rendered content.
 *
 * Strings returned by the renderer are passed through `setSanitizedHTML()` —
 * mirroring the cell render path — so a detail template interpolating row data
 * cannot become an XSS sink.
 */
export function createDetailElement(
  row: any,
  rowIndex: number,
  renderer: (row: any, rowIndex: number) => HTMLElement | string,
  columnCount: number,
): HTMLElement {
  const detailRow = document.createElement('div');
  detailRow.className = 'master-detail-row';
  detailRow.setAttribute('data-detail-for', String(rowIndex));
  detailRow.setAttribute('role', 'row');

  const detailCell = document.createElement('div');
  detailCell.className = 'master-detail-cell';
  detailCell.setAttribute('role', 'cell');
  detailCell.style.gridColumn = `1 / ${columnCount + 1}`;

  const content = renderer(row, rowIndex);
  if (typeof content === 'string') {
    setSanitizedHTML(detailCell, content);
  } else if (content instanceof HTMLElement) {
    detailCell.appendChild(content);
  }

  detailRow.appendChild(detailCell);
  return detailRow;
}
